<!--
  Read-only «Справочники» / parent field: teal stripe, caption, value pill (drawer-consistent text),
  copy uses raw leaf value; pill click opens all-fields drawer at this field.
-->
<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { VIcons } from '@/shared/ui'
import { useTranslate } from '@/shared/lib'
import { shouldShowPassportRuntimeValueTooltip } from '../model/passportRuntimeFieldDisplay'

const props = withDefaults(
  defineProps<{
    caption: string
    /** Single-line pill label (name or status-only, per drawer rules). */
    pillText: string
    /** Raw string for clipboard (leaf `fieldValues` entry). */
    copyValue: string
    /** Inventory field `type` (e.g. `string`) — tooltip gating. */
    fieldType: string
    /** Grid cell `palette_source` — tooltip gating. */
    layoutPaletteSource?: string | null
    /** When false the pill is not clickable and does not open the all-fields drawer. */
    clickable?: boolean
    /** Подсветка после failed save (обязательное пустое поле). */
    showMandatoryError?: boolean
  }>(),
  {
    layoutPaletteSource: null,
    clickable: true,
    showMandatoryError: false,
  },
)

const emit = defineEmits<{ openDrawer: [] }>()

const { trs } = useTranslate()
const copied = ref(false)
const toastOpen = ref(false)
let resetTimer: ReturnType<typeof setTimeout> | undefined

onBeforeUnmount(() => {
  if (resetTimer) clearTimeout(resetTimer)
})

async function onCopy(e: MouseEvent) {
  e.stopPropagation()
  const raw = props.copyValue ?? ''
  if (raw === '') {
    toastOpen.value = true
    return
  }
  try {
    await navigator.clipboard.writeText(raw)
    copied.value = true
    if (resetTimer) clearTimeout(resetTimer)
    resetTimer = setTimeout(() => {
      copied.value = false
      resetTimer = undefined
    }, 1600)
  } catch {
    copied.value = false
  }
}

function onPillClick() {
  if (!props.clickable) return
  emit('openDrawer')
}

const showValueTooltip = computed(() =>
  shouldShowPassportRuntimeValueTooltip({
    fieldType: props.fieldType,
    layoutPaletteSource: props.layoutPaletteSource,
    displayText: props.pillText,
  }),
)
</script>

<template>
  <div
    class="passport-runtime-v2__parent-field-card"
    :class="{ 'passport-runtime-v2__parent-field-card--mandatory-error': showMandatoryError }"
  >
    <div class="passport-runtime-v2__parent-field-card__main">
      <div class="passport-runtime-v2__parent-field-card__caption" :title="caption">
        {{ caption }}
      </div>
      <VTooltip location="top" :eager="false" :disabled="!showValueTooltip">
        <template #activator="{ props: tipProps }">
          <span
            v-bind="tipProps"
            class="passport-runtime-v2__parent-field-card__value passport-runtime-v2__ellipsis"
            :class="{
              'passport-runtime-v2__parent-field-card__value--clickable': clickable,
              'passport-runtime-v2__parent-field-card__value--error': showMandatoryError,
            }"
            :role="clickable ? 'button' : undefined"
            :tabindex="clickable ? -1 : undefined"
            @click="onPillClick"
          >
            {{ pillText }}
          </span>
        </template>
        <span>{{ pillText }}</span>
      </VTooltip>
      <div
        v-if="showMandatoryError"
        class="passport-runtime-v2__parent-field-card__error"
      >
        {{ trs('validate.required') }}
      </div>
    </div>
    <button
      type="button"
      class="passport-runtime-v2__parent-field-card__copy"
      :aria-label="trs('itf.copy')"
      @click.stop="onCopy"
    >
      <VIcons
        v-if="!copied"
        type="content_copy"
        class="passport-runtime-v2__parent-field-card__icon"
      />
      <VIcons v-else type="check" class="passport-runtime-v2__parent-field-card__icon" />
    </button>
    <VSnackbar v-model="toastOpen" location="bottom" :timeout="2200" color="surface" variant="tonal">
      {{ trs('itf.passport_runtime_copy_empty_toast') }}
    </VSnackbar>
  </div>
</template>

<style scoped>
/* Layout-critical + copy-icon rules — inline scoped so they ship in the component chunk.
 * Mirrors PassportRuntimeUnsupportedFieldCard (no @import — Vite may extract imports to a
 * late chunk). Visual chrome (border/bg/padding/typography) lives in page-level style.scss. */
.passport-runtime-v2__parent-field-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  min-width: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.passport-runtime-v2__parent-field-card__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.passport-runtime-v2__parent-field-card__copy {
  flex: 0 0 auto;
  align-self: center;
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  touch-action: manipulation;
  color: var(--prv2-parent-dictionary-copy, rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity))) !important;
}

.passport-runtime-v2__parent-field-card__copy :deep(.passport-runtime-v2__parent-field-card__icon.material-symbols-rounded.v-icon),
.passport-runtime-v2__parent-field-card__copy :deep(span.material-symbols-rounded.v-icon) {
  font-size: 18px;
  width: 18px;
  height: 18px;
  color: rgb(var(--v-theme-primary)) !important;
  opacity: 1 !important;
}

.passport-runtime-v2__parent-field-card__value--clickable {
  cursor: pointer;
  min-width: 0;
}

.passport-runtime-v2__parent-field-card--mandatory-error
  .passport-runtime-v2__parent-field-card__caption {
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__parent-field-card__value--error {
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__parent-field-card__error {
  margin-top: 4px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}

.passport-runtime-v2__ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
